import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  X,
  Briefcase,
  Wind,
  Sun,
  Tool,
  Droplet,
  Home,
  Scissors,
  Zap,
  Truck,
  Trash2,
  Settings,
  Thermometer,
  CloudSnow,
  CloudRain,
  Feather,
  Star,
  Heart,
  Shield,
  Award,
  Box,
  Package,
  PenTool,
  Edit3,
  Layers,
  Grid,
  Circle,
  HelpCircle
} from 'react-feather';
import { API_BASE_URL } from '../../config/api';
import { authFetch } from '../../utils/authFetch';

// Map icon names to React Feather components
const iconMap: { [key: string]: React.ComponentType<{ size?: number; color?: string }> } = {
  briefcase: Briefcase,
  wind: Wind,
  sun: Sun,
  tool: Tool,
  droplet: Droplet,
  home: Home,
  scissors: Scissors,
  zap: Zap,
  truck: Truck,
  trash: Trash2,
  trash2: Trash2,
  settings: Settings,
  thermometer: Thermometer,
  snow: CloudSnow,
  cloudsnow: CloudSnow,
  rain: CloudRain,
  cloudrain: CloudRain,
  leaf: Feather,
  feather: Feather,
  flower: Star,
  star: Star,
  heart: Heart,
  shield: Shield,
  award: Award,
  box: Box,
  package: Package,
  wrench: Tool,
  hammer: Tool,
  pentool: PenTool,
  edit: Edit3,
  layers: Layers,
  grid: Grid,
  circle: Circle,
  search: Search,
  helpcircle: HelpCircle
};

const getIconComponent = (iconName?: string): React.ComponentType<{ size?: number; color?: string }> | null => {
  if (!iconName) return null;
  const normalizedName = iconName.toLowerCase().replace(/[^a-z0-9]/g, '');
  return iconMap[normalizedName] || null;
};

interface Service {
  id: number;
  name: string;
  icon?: string;
  description?: string;
  isOther?: boolean;
}

interface ServiceSearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (service: Service | null) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  autoFocus?: boolean;
  showClearButton?: boolean;
}

const ServiceSearchAutocomplete: React.FC<ServiceSearchAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Search services...',
  style,
  autoFocus = false,
  showClearButton = true
}) => {
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      setIsLoading(true);
      try {
        const response = await authFetch(`${API_BASE_URL}/services`);
        if (response.ok) {
          const data = await response.json();
          const servicesList = data.services || data;
          if (Array.isArray(servicesList)) {
            setServices(servicesList);
          }
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Filter services based on input
  useEffect(() => {
    if (!value.trim()) {
      setFilteredServices(services);
    } else {
      const searchTerm = value.toLowerCase();
      const filtered = services.filter(service =>
        service.name.toLowerCase().includes(searchTerm)
      );
      setFilteredServices(filtered);
    }
    setHighlightedIndex(-1);
  }, [value, services]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleServiceSelect = useCallback((service: Service) => {
    onChange(service.name);
    onSelect(service);
    setIsOpen(false);
    inputRef.current?.blur();
  }, [onChange, onSelect]);

  const handleClear = () => {
    onChange('');
    onSelect(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredServices.length ? prev + 1 : prev  // Include "Other" option at index length
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === filteredServices.length) {
          // "Other" option selected
          const otherService: Service = {
            id: -1,
            name: 'Other',
            icon: 'helpcircle',
            description: 'Describe your custom service need',
            isOther: true
          };
          onChange(otherService.name);
          onSelect(otherService);
          setIsOpen(false);
        } else if (highlightedIndex >= 0 && filteredServices[highlightedIndex]) {
          handleServiceSelect(filteredServices[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Input Container */}
      <div style={{ position: 'relative' }}>
        <Search
          size={18}
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            pointerEvents: 'none'
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            padding: '12px 40px 12px 40px',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '16px',
            outline: 'none',
            backgroundColor: 'white',
            cursor: 'text',
            transition: 'border-color 0.2s',
            ...style
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLInputElement).style.borderColor = '#cbd5e1';
          }}
          onMouseLeave={(e) => {
            if (document.activeElement !== e.target) {
              (e.target as HTMLInputElement).style.borderColor = '#e2e8f0';
            }
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
          }}
        />
        {showClearButton && value && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              minHeight: '44px',
              minWidth: '44px'
            }}
            type="button"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 1000
          }}
        >
          {isLoading ? (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '14px'
            }}>
              Loading services...
            </div>
          ) : filteredServices.length === 0 ? (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              color: '#64748b',
              fontSize: '14px'
            }}>
              No services found — press Search to find contractors matching "{value}"
            </div>
          ) : (
            <>
              {filteredServices.map((service, index) => (
                <div
                  key={service.id}
                  onClick={() => handleServiceSelect(service)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    backgroundColor: highlightedIndex === index ? '#f1f5f9' : 'white',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    transition: 'background-color 0.15s',
                    textAlign: 'left'
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {service.icon && (() => {
                    const IconComponent = getIconComponent(service.icon);
                    return IconComponent ? (
                      <IconComponent size={20} color="#64748b" />
                    ) : (
                      <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '2px' }}>{service.icon}</span>
                    );
                  })()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1e293b',
                      textAlign: 'left'
                    }}>
                      {service.name}
                    </div>
                    {service.description && (
                      <div style={{
                        fontSize: '13px',
                        color: '#64748b',
                        marginTop: '3px',
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                        {service.description.length > 80
                          ? service.description.substring(0, 80) + '...'
                          : service.description}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {/* "Other" option - always visible at bottom */}
              <div
                onClick={() => {
                  const otherService: Service = {
                    id: -1,
                    name: 'Other',
                    icon: 'helpcircle',
                    description: 'Describe your custom service need',
                    isOther: true
                  };
                  onChange(otherService.name);
                  onSelect(otherService);
                  setIsOpen(false);
                  inputRef.current?.blur();
                }}
                style={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                  backgroundColor: highlightedIndex === filteredServices.length ? '#f1f5f9' : '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={() => setHighlightedIndex(filteredServices.length)}
              >
                <HelpCircle size={20} color="#64748b" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#1e293b',
                    textAlign: 'left'
                  }}>
                    Other
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#64748b',
                    marginTop: '3px',
                    textAlign: 'left'
                  }}>
                    Can't find what you need? Describe your request
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceSearchAutocomplete;
